import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t, tDyn } from '../i18n';
import { clearOfflineData } from '../offline';
import { getOfflineState } from '../offline/state';

export function CampaignsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: api.campaigns });
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });
  // Wie der Server: Admins und alle, die irgendwo DM/Co-DM sind
  const canCreate = !!auth.data?.user?.isAdmin || !!campaigns.data?.some((c) => c.role !== 'player');
  const [name, setName] = useState('');
  const [ruleset, setRuleset] = useState('mixed');
  const [error, setError] = useState<string | null>(null);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const c = await api.createCampaign(name, ruleset);
      await qc.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/c/${c.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const logout = async () => {
    const { pending } = getOfflineState();
    if (pending > 0 && !confirm(t('offline.logoutPending', { n: pending }))) return;
    try {
      await api.logout();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      return;
    }
    // Lokale Kopie (auch DM-Geheimnisse) nicht auf dem Gerät lassen
    await clearOfflineData();
    // Erst abgemeldet anzeigen, dann den Rest leeren – `clear()` allein lädt den Anmeldestatus nicht neu
    qc.setQueryData(['auth'], { needsSetup: false, user: null });
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
  };

  return (
    <div className="page-narrow">
      <header className="page-head">
        <h1>
          <span className="logo">▤</span> {t('campaigns.title')}
        </h1>
        <span className="row">
          <Link className="button" to="/account">
            {t('auth.account')}
          </Link>
          <button onClick={logout}>{t('auth.logout')}</button>
        </span>
      </header>
      {campaigns.data?.length === 0 && <p className="muted">{t('campaigns.empty')}</p>}
      <ul className="campaign-list">
        {campaigns.data?.map((c) => (
          <li key={c.id}>
            <Link to={`/c/${c.id}`}>
              <strong>{c.name}</strong>
              <span className="muted small">
                {tDyn(`ruleset.${c.ruleset}`)} · {t('campaigns.notes', { n: c.noteCount ?? 0 })}
                {c.role !== 'dm' && <> · {tDyn(`role.${c.role}`)}</>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {canCreate && (
        <form className="card row-form" onSubmit={create}>
          <h2>{t('campaigns.new')}</h2>
          <input placeholder={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <select value={ruleset} onChange={(e) => setRuleset(e.target.value)} aria-label={t('campaigns.ruleset')}>
            {['5e-2014', '5e-2024', 'mixed'].map((r) => (
              <option key={r} value={r}>
                {tDyn(`ruleset.${r}`)}
              </option>
            ))}
          </select>
          <button className="primary">{t('common.create')}</button>
          {error && <div className="error">{error}</div>}
        </form>
      )}
      {canCreate && (
        <p>
          <Link to="/import">→ {t('campaigns.import')}</Link>
        </p>
      )}
      {!canCreate && error && <div className="error">{error}</div>}
    </div>
  );
}
